/**
 * Losi App Capabilities
 *
 * Maps app actions to tool-aware execution capabilities.
 *
 * Primary responsibilities:
 * - Define capability metadata used by generated app actions
 * - Resolve action execution strategies with safe fallbacks
 *
 * Dependencies:
 * - zod
 * - App definition action types
 */

import { z } from 'zod'

import type { AppAction, AppActionType } from '@/lib/apps/definition'

export const appCapabilityKeys = [
  'ai.generateText',
  'ai.review',
  'ai.summarize',
  'ai.prioritize',
  'web.fetch',
  'browser.openAndInspect',
  'screenshot.capture',
  'losiComputer.browserSession',
  'file.read',
  'file.create',
  'image.generate',
  'image.edit',
  'spaces.createTask',
  'spaces.createNote',
  'nexus.createLead',
  'manual.input',
  'approval.request'
] as const

export const appCapabilityAvailabilityStatuses = [
  'available',
  'requires_connection',
  'requires_approval',
  'manual',
  'limited'
] as const

export const appCapabilityReliabilityLevels = ['high', 'medium', 'low'] as const

export const appActionFailureModes = [
  'capability_unavailable',
  'auth_required',
  'approval_required',
  'bot_blocked',
  'captcha',
  'incomplete_content',
  'input_required',
  'javascript_required',
  'login_required',
  'provider_error',
  'screenshot_unavailable',
  'timeout',
  'unsafe_request',
  'low_reliability'
] as const

export const appActionOnFailureOptions = [
  'try_fallback',
  'request_input',
  'request_approval',
  'manual_handoff',
  'stop'
] as const

const appCapabilityKeySchema = z.enum(appCapabilityKeys)
const appActionFailureModeSchema = z.enum(appActionFailureModes)
const appActionOnFailureSchema = z.enum(appActionOnFailureOptions)

const appCapabilityFallbacksSchema = z
  .array(appCapabilityKeySchema)
  .max(8)
  .refine((fallbacks) => new Set(fallbacks).size === fallbacks.length, {
    message: 'Fallback capabilities must be unique'
  })

export const appActionExecutionStrategySchema = z
  .object({
    preferredCapability: appCapabilityKeySchema,
    fallbacks: appCapabilityFallbacksSchema.default([]),
    failureModes: z.array(appActionFailureModeSchema).max(8).default([]),
    onFailure: appActionOnFailureSchema.default('try_fallback')
  })
  .superRefine((strategy, ctx) => {
    if (!strategy.fallbacks.includes(strategy.preferredCapability)) return

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Fallback capabilities must not repeat the preferred capability',
      path: ['fallbacks']
    })
  })

export type AppCapabilityKey = (typeof appCapabilityKeys)[number]
export type AppCapabilityAvailability = (typeof appCapabilityAvailabilityStatuses)[number]
export type AppCapabilityReliability = (typeof appCapabilityReliabilityLevels)[number]
export type AppActionFailureMode = (typeof appActionFailureModes)[number]
export type AppActionOnFailure = (typeof appActionOnFailureOptions)[number]
export type AppActionExecutionStrategy = z.infer<typeof appActionExecutionStrategySchema>

export interface AppCapabilityDefinition {
  key: AppCapabilityKey
  label: string
  availability: AppCapabilityAvailability
  reliability: AppCapabilityReliability
  fallbackCapabilities: AppCapabilityKey[]
  failureModes: AppActionFailureMode[]
  onFailure: AppActionOnFailure
}

export interface ResolvedAppActionExecutionStrategy extends AppActionExecutionStrategy {
  capability: AppCapabilityDefinition
}

export const APP_CAPABILITY_REGISTRY: Record<AppCapabilityKey, AppCapabilityDefinition> = {
  'ai.generateText': capability('ai.generateText', 'Generate text', 'available', 'high', {
    fallbackCapabilities: ['manual.input'],
    failureModes: ['provider_error', 'input_required']
  }),
  'ai.review': capability('ai.review', 'Review', 'available', 'medium', {
    fallbackCapabilities: ['ai.summarize', 'ai.generateText', 'manual.input'],
    failureModes: ['provider_error', 'input_required', 'low_reliability']
  }),
  'ai.summarize': capability('ai.summarize', 'Summarize', 'available', 'high', {
    fallbackCapabilities: ['ai.generateText', 'manual.input'],
    failureModes: ['provider_error', 'input_required']
  }),
  'ai.prioritize': capability('ai.prioritize', 'Prioritize', 'available', 'high', {
    fallbackCapabilities: ['ai.review', 'manual.input'],
    failureModes: ['provider_error', 'input_required']
  }),
  'web.fetch': capability('web.fetch', 'Fetch web page', 'limited', 'medium', {
    fallbackCapabilities: ['browser.openAndInspect', 'manual.input'],
    failureModes: [
      'bot_blocked',
      'captcha',
      'incomplete_content',
      'javascript_required',
      'login_required',
      'timeout'
    ]
  }),
  'browser.openAndInspect': capability(
    'browser.openAndInspect',
    'Open and inspect browser page',
    'requires_connection',
    'medium',
    {
      fallbackCapabilities: ['web.fetch', 'screenshot.capture', 'manual.input'],
      failureModes: [
        'bot_blocked',
        'captcha',
        'javascript_required',
        'login_required',
        'provider_error',
        'timeout'
      ]
    }
  ),
  'screenshot.capture': capability(
    'screenshot.capture',
    'Capture screenshot',
    'requires_connection',
    'medium',
    {
      fallbackCapabilities: ['browser.openAndInspect', 'manual.input'],
      failureModes: [
        'capability_unavailable',
        'auth_required',
        'provider_error',
        'screenshot_unavailable'
      ]
    }
  ),
  'losiComputer.browserSession': capability(
    'losiComputer.browserSession',
    'Losi computer browser session',
    'requires_connection',
    'medium',
    {
      fallbackCapabilities: [
        'browser.openAndInspect',
        'web.fetch',
        'screenshot.capture',
        'manual.input'
      ],
      failureModes: [
        'bot_blocked',
        'captcha',
        'incomplete_content',
        'javascript_required',
        'login_required',
        'provider_error',
        'screenshot_unavailable',
        'timeout'
      ]
    }
  ),
  'file.read': capability('file.read', 'Read file', 'requires_connection', 'high', {
    fallbackCapabilities: ['manual.input'],
    failureModes: ['capability_unavailable', 'auth_required', 'input_required']
  }),
  'file.create': capability('file.create', 'Create file', 'available', 'high', {
    fallbackCapabilities: ['manual.input'],
    failureModes: ['provider_error', 'input_required']
  }),
  'image.generate': capability('image.generate', 'Generate image', 'available', 'medium', {
    fallbackCapabilities: ['ai.generateText', 'manual.input'],
    failureModes: ['capability_unavailable', 'provider_error', 'input_required']
  }),
  'image.edit': capability('image.edit', 'Edit image', 'available', 'medium', {
    fallbackCapabilities: ['image.generate', 'manual.input'],
    failureModes: ['capability_unavailable', 'provider_error', 'input_required']
  }),
  'spaces.createTask': capability('spaces.createTask', 'Create Space task', 'requires_connection', 'high', {
    fallbackCapabilities: ['approval.request', 'manual.input'],
    failureModes: ['capability_unavailable', 'auth_required', 'approval_required'],
    onFailure: 'request_approval'
  }),
  'spaces.createNote': capability('spaces.createNote', 'Create Space note', 'requires_connection', 'high', {
    fallbackCapabilities: ['file.create', 'manual.input'],
    failureModes: ['capability_unavailable', 'auth_required', 'approval_required'],
    onFailure: 'request_approval'
  }),
  'nexus.createLead': capability('nexus.createLead', 'Create Nexus lead', 'requires_connection', 'medium', {
    fallbackCapabilities: ['manual.input'],
    failureModes: ['capability_unavailable', 'auth_required', 'input_required']
  }),
  'manual.input': capability('manual.input', 'Manual input', 'manual', 'high', {
    fallbackCapabilities: [],
    failureModes: ['input_required'],
    onFailure: 'request_input'
  }),
  'approval.request': capability('approval.request', 'Request approval', 'requires_approval', 'high', {
    fallbackCapabilities: ['manual.input'],
    failureModes: ['approval_required'],
    onFailure: 'request_approval'
  })
}

const defaultActionCapabilityByType: Record<AppActionType, AppCapabilityKey> = {
  'ai.classify': 'ai.review',
  'ai.extractFromText': 'ai.summarize',
  'ai.generateChecklist': 'ai.generateText',
  'ai.generateCopy': 'ai.generateText',
  'ai.generateImagePrompt': 'ai.generateText',
  'ai.generateReport': 'ai.generateText',
  'ai.generateText': 'ai.generateText',
  'ai.prioritize': 'ai.prioritize',
  'ai.review': 'ai.review',
  'ai.summarize': 'ai.summarize',
  'approval.request': 'approval.request',
  'file.create': 'file.create',
  'image.edit': 'image.edit',
  'image.generate': 'image.generate',
  'spaces.createNote': 'spaces.createNote',
  'spaces.createNoteDraft': 'ai.generateText',
  'spaces.createTask': 'spaces.createTask',
  'spaces.createTaskDraft': 'ai.generateText',
  'web.fetch': 'web.fetch'
}

// --------------

export function getDefaultActionCapabilityKey(type: AppActionType): AppCapabilityKey {
  return defaultActionCapabilityByType[type]
}

// --------------

export function createDefaultActionExecutionStrategy(type: AppActionType): AppActionExecutionStrategy {
  const preferredCapability = getDefaultActionCapabilityKey(type)
  const capabilityDefinition = APP_CAPABILITY_REGISTRY[preferredCapability]

  return {
    preferredCapability,
    fallbacks: dedupeCapabilities(capabilityDefinition.fallbackCapabilities),
    failureModes: [...capabilityDefinition.failureModes],
    onFailure: capabilityDefinition.onFailure
  }
}

// --------------

export function resolveAppActionExecutionStrategy(
  action: Pick<AppAction, 'type' | 'executionStrategy'>
): ResolvedAppActionExecutionStrategy {
  const preferredCapability =
    action.executionStrategy?.preferredCapability ?? getDefaultActionCapabilityKey(action.type)
  const capabilityDefinition = APP_CAPABILITY_REGISTRY[preferredCapability]
  const configuredFallbacks = action.executionStrategy?.fallbacks ?? []
  const fallbacks = dedupeCapabilities([
    ...configuredFallbacks,
    ...capabilityDefinition.fallbackCapabilities
  ]).filter((capabilityKey) => capabilityKey !== preferredCapability)
  const failureModes = action.executionStrategy?.failureModes?.length
    ? action.executionStrategy.failureModes
    : capabilityDefinition.failureModes

  return {
    preferredCapability,
    fallbacks,
    failureModes: [...failureModes],
    onFailure: action.executionStrategy?.onFailure ?? capabilityDefinition.onFailure,
    capability: capabilityDefinition
  }
}

// --------------

function capability(
  key: AppCapabilityKey,
  label: string,
  availability: AppCapabilityAvailability,
  reliability: AppCapabilityReliability,
  options: {
    fallbackCapabilities: AppCapabilityKey[]
    failureModes: AppActionFailureMode[]
    onFailure?: AppActionOnFailure
  }
): AppCapabilityDefinition {
  return {
    key,
    label,
    availability,
    reliability,
    fallbackCapabilities: options.fallbackCapabilities,
    failureModes: options.failureModes,
    onFailure: options.onFailure ?? 'try_fallback'
  }
}

// --------------

function dedupeCapabilities(capabilityKeys: AppCapabilityKey[]): AppCapabilityKey[] {
  return [...new Set(capabilityKeys)]
}
